import * as S from "./EquipCurrentContent.style";
import dayjs from "dayjs";
import { ColDef } from "ag-grid-community";
import { useLocation, useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction } from "react";
import { Buttons } from "../../../atoms/Buttons";
import { BarChart } from "../../../atoms/Charts/BarChart";
import { LedLegendItem } from "../../../atoms/LegendItem/LedLegendItem";
import { EquipCurrentStatus } from "../../../atoms/EquipCurrentStatus";
import { Grid } from "../../../molecules/Grid";
import { ContentBox } from "../../../molecules/ContentBox";
import { ContentTabList } from "../../../molecules/ContentTabList";
import DiagnosisContextMenu from "../../../molecules/DiagnosisContextMenu";
import ImgGenerator from "../../../../styles/assets/img/img_generator.png";
import ImgGasTurbine from "../../../../styles/assets/img/img_gasturbine.png";
import ImgPropmotor from "../../../../styles/assets/img/img_propmotor.png";
import ImgCpp from "../../../../styles/assets/img/img_cpp.png";
import ImgPms from "../../../../styles/assets/img/img_pms.png";
import ImgRGear from "../../../../styles/assets/img/img_rgear.png";
import ImgCooler from "../../../../styles/assets/img/img_cooler.png";
import {
  AlarmActionStatusDataType,
  EquipmentAlarmStatus,
  EquipmentOperationStatusTimeType,
} from "../../../../common/types/equipment-monitoring";
import useDiagnosisContextMenu from "../../../../common/hooks/useDiagnosisContextMenu";
import { ReactComponent as IconChart } from "../../../../styles/assets/svg/icon_graph-bar.svg";
import { routerNameChanger } from "../../../../common/util/routerNameChanger";
import {
  mcyStatusChanger,
  mcyStatusChangerName,
} from "../../../../common/util/myStatuschange";
import { TextInfo } from "../../../atoms/TextInfo";

interface EquipCurrentContentProps {
  equipmentOperationStatusTime?: EquipmentOperationStatusTimeType;
  alarmStatusData?: EquipmentAlarmStatus;
  sensorAlarmData?: any;
  alarmActionStatusData?: AlarmActionStatusDataType;
  alarmStatusTimeTab?: number;
  setAlarmStatusTimeTab?: Dispatch<SetStateAction<number>>;
  alarmActionStatusTimeTab?: number;
  setAlarmActionStatusTimeTab?: Dispatch<SetStateAction<number>>;
  equipmentOperationStatusLED?: number;
  onLowMachinaryGraphClicked: (params: any) => void;
}

export const EquipCurrentContent = ({
  equipmentOperationStatusTime,
  alarmStatusData,
  sensorAlarmData,
  alarmStatusTimeTab,
  setAlarmStatusTimeTab,
  equipmentOperationStatusLED,
  onLowMachinaryGraphClicked,
}: EquipCurrentContentProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const onNavigateChart = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconChart />}
        onClick={() => {
          navigate(
            `/equipment/${
              routerNameChanger?.(e?.data.ste_mcy_name).routerName
            }/1`,
            {
              state: {
                search: {
                  shipTypeNo: e?.data?.ste_shiptype_no,
                  shipNo: e?.data?.ste_ship_no,
                  mcyNo: routerNameChanger?.(e?.data.ste_mcy_name).mcyCode,
                  mcyName: e?.data?.ste_mcy_name,
                  lowMcyName: e?.data?.ste_low_mcy_name,
                  sensorCode: e?.data?.ste_sensor_code,
                  startDate: dayjs(e?.data?.ste_alarm_time)
                    .subtract(7, "days")
                    .format("YYYY-MM-DD HH:mm:ss"),
                  endDate: dayjs(e?.data?.ste_alarm_time).format(
                    "YYYY-MM-DD HH:mm:ss"
                  ),
                },
              },
            }
          );
        }}
      />
    );
  };

  // 센서 알림현황 레벨 뱃지 렌더러
  const legendItemRenderer = (params: any) => {
    const alarmType = params.data.ste_alarm_type;

    switch (alarmType) {
      case 0:
        return (
          <LedLegendItem
            ledColor="green"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      case 1:
        return (
          <LedLegendItem
            ledColor="yellow"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      case 2:
        return (
          <LedLegendItem
            ledColor="red"
            title=""
            onContextMenu={(e) => handleContextMenu(e, params.data)}
          />
        );
      default:
        return;
      // return <LedLegendItem ledColor="green" title="" />;
    }
  };

  const { contextMenu, handleContextMenu, handleOpenInNewTab } =
    useDiagnosisContextMenu();

  const tabs = [
    {
      index: 0,
      name: "전1주",
    },
    {
      index: 1,
      name: "전월",
    },
    {
      index: 2,
      name: "전3개월",
    },
  ];

  const columnDefs: ColDef[] = [
    {
      headerName: "센서 알람 시간",
      field: "ste_alarm_time",
      width: 160,
      cellRenderer: (params: any) => {
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      headerName: "센서명",
      field: "ste_sensor_name",
      flex: 1,
    },
    {
      headerName: "레벨",
      field: "level",
      width: 70,
      cellRenderer: legendItemRenderer,
    },
    {
      headerName: "차트",
      field: "chart",
      width: 70,
      cellRenderer: onNavigateChart,
    },
  ];

  return (
    <S.EquipCurrentContent>
      <S.EquipCurrentTitle>장비 현황</S.EquipCurrentTitle>

      <S.EquipContentBox>
        {/* 장비 운용현황 */}
        <ContentBox
          title="장비 운용현황"
          topbar={
            equipmentOperationStatusTime ? (
              <EquipCurrentStatus
                time={
                  dayjs(equipmentOperationStatusTime?.time).format(
                    "YYYY-MM-DD HH:mm:ss"
                  ) || ""
                } // 현재 시간
                powerOn={equipmentOperationStatusTime?.powerOn}
                statNormal={mcyStatusChanger(equipmentOperationStatusLED)}
                stateName={mcyStatusChangerName(equipmentOperationStatusLED)}
              />
            ) : (
              <div>loading..</div>
            )
          }
          shade={true}
          gridRow="span 2"
        >
          <>
            <img
              src={
                location.pathname.includes("/equipment/gas-turbine")
                  ? ImgGasTurbine
                  : ImgGenerator
              }
              alt="NO.1 발전기 이미지"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center",
              }}
            />
          </>
        </ContentBox>

        {/* 센서 알람현황 */}
        <ContentBox title="센서 알람현황" shade={false}>
          <Grid
            rowData={sensorAlarmData}
            columnDefs={columnDefs}
            defaultPageSize={10}
            pagingShow={true}
          />
        </ContentBox>

        {/* 계통별 알람현황 */}
        <ContentBox
          title="계통별 알람현황"
          topbar={
            <div>
              <TextInfo
                title={
                  alarmStatusData?.startDate && alarmStatusData?.endDate
                    ? `${alarmStatusData.startDate}~${alarmStatusData.endDate}`
                    : "날짜 정보를 불러오는 중..."
                }
              />
              <ContentTabList
                data={tabs}
                selectedTab={alarmStatusTimeTab}
                setSelectedTab={setAlarmStatusTimeTab}
              />
            </div>
          }
          shade={false}
        >
          {alarmStatusData &&
          (alarmStatusData?.section.length > 0 ||
            alarmStatusData?.value.length > 0) ? (
            <BarChart
              data={alarmStatusData}
              onLowMachinaryGraphClicked={onLowMachinaryGraphClicked}
            />
          ) : (
            <S.EquipDescriptionText>
              조회된 데이터가 없습니다.
            </S.EquipDescriptionText>
          )}
        </ContentBox>
      </S.EquipContentBox>
      <DiagnosisContextMenu
        handleOpenInNewTab={handleOpenInNewTab}
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
      />
    </S.EquipCurrentContent>
  );
};
